import { AccountInfo, AuthenticationResult, IPublicClientApplication } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';

const API_SCOPES = loginRequest.scopes ?? ['User.Read', 'email', 'profile', 'openid'];

/** Renova antes do vencimento para absorver diferença de relógio entre navegador e backend. */
const EXPIRATION_SKEW_SECONDS = 120;

function decodeJwtPayload(token: string): Record<string, unknown> | null {
    try {
        const payload = token.split('.')[1];
        if (!payload) return null;
        return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
        return null;
    }
}

function isTokenUsable(token?: string | null): boolean {
    if (!token) return false;
    const exp = decodeJwtPayload(token)?.exp;
    // Sem exp legível, deixa a validação para o backend.
    if (typeof exp !== 'number') return true;
    return exp - EXPIRATION_SKEW_SECONDS > Date.now() / 1000;
}

function resolveAccount(instance: IPublicClientApplication): AccountInfo {
    const active = instance.getActiveAccount();
    if (active) return active;

    const accounts = instance.getAllAccounts();
    if (accounts.length === 0) {
        throw new Error('Nenhuma conta encontrada. Por favor, faça login novamente.');
    }
    instance.setActiveAccount(accounts[0]);
    return accounts[0];
}

async function acquireSilent(
    instance: IPublicClientApplication,
    account: AccountInfo,
    forceRefresh: boolean,
): Promise<AuthenticationResult> {
    try {
        return await instance.acquireTokenSilent({ scopes: API_SCOPES, account, forceRefresh });
    } catch (tokenError: any) {
        if (tokenError?.name === 'InteractionRequiredAuthError') {
            return instance.acquireTokenPopup({ scopes: API_SCOPES, account });
        }
        throw new Error(`Erro ao obter token: ${tokenError?.message ?? tokenError}`);
    }
}

export async function getMsalApiToken(
    instance: IPublicClientApplication,
    forceInteractive = false,
    forceRefresh = false,
): Promise<string> {
    const account = resolveAccount(instance);

    let tokenResponse = forceInteractive
        ? await instance.acquireTokenPopup({ scopes: API_SCOPES, account })
        : await acquireSilent(instance, account, forceRefresh);

    // O ID token vem do cache enquanto o access token continuar válido, então pode chegar
    // vencido ao backend; nesse caso força a renovação em vez de enviar um token morto.
    if (!forceInteractive && !isTokenUsable(tokenResponse.idToken)) {
        tokenResponse = await acquireSilent(instance, account, true);
    }

    const apiToken = tokenResponse.idToken;
    if (!apiToken) {
        throw new Error('Não foi possível obter token de autenticação. Faça login novamente.');
    }

    return apiToken;
}
